export interface User {
  id: string;
  email: string;
  role: string;
}

export interface AuthResponse {
  token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface SceneIllustration {
  asset_url?: string;
  aspect_ratio: string;
  style: string;
  prompt_fr: string;
  prompt_en: string;
}

export interface Scene {
  scene_number: number;
  phase: string;
  title?: string;
  text: string;
  estimated_duration_seconds: number;
  audio_start_offset_seconds: number;
  audio_url: string;
  illustration: SceneIllustration;
}

export interface AudioConfig {
  format: string;
  url?: string;
  provider?: string;
  voice_name?: string;
  voice_id?: string;
  model_id?: string;
  voice_style: string;
  tempo_words_per_minute: number;
  background_ambience?: string;
}

export interface Story {
  id: string;
  publication_date: string;
  target_age_group: string;
  title: string;
  synopsis: string;
  moral: string;
  reading_duration_seconds: number;
  author_credits?: string;
  audio: AudioConfig;
  scenes: Scene[];
  publish_at?: string;
  published?: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const apiClient = {
  getAuthToken(): string | null {
    return localStorage.getItem('contedusoir_jwt_token');
  },

  getUser(): User | null {
    const raw = localStorage.getItem('contedusoir_jwt_user');
    return raw ? JSON.parse(raw) : null;
  },

  setSession(authData: AuthResponse) {
    localStorage.setItem('contedusoir_jwt_token', authData.token);
    localStorage.setItem('contedusoir_jwt_user', JSON.stringify(authData.user));
  },

  clearSession() {
    localStorage.removeItem('contedusoir_jwt_token');
    localStorage.removeItem('contedusoir_jwt_user');
  },

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    const res = await fetch(API_BASE_URL + '/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || "Échec de l'authentification");
    }

    this.setSession(data);
    return data;
  },

  async fetchTodayStory(): Promise<Story> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }

    const res = await fetch(API_BASE_URL + '/stories/today', { headers });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || 'Impossible de charger le conte du jour');
    }
    return data;
  },

  async fetchCatalog(targetAgeGroup?: string): Promise<{ total: number; stories: Story[] }> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }

    let url = API_BASE_URL + '/stories/catalog';
    if (targetAgeGroup) {
      url += '?target_age_group=' + encodeURIComponent(targetAgeGroup);
    }

    const res = await fetch(url, { headers });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || 'Impossible de charger le catalogue');
    }
    return data;
  }
};
